/**
 * ProfileCompletionBanner — top-of-page nag for users whose profile is
 * incomplete (legacy accounts that predate the mandatory pro/amateur
 * field, or admin-created users).
 *
 * The banner:
 *   1. Reads the current user from AuthContext.
 *   2. Renders only when `profile_type` is missing (null / undefined /
 *      empty). Once completed the whole component unmounts so the layout
 *      shifts back to normal — no lingering "you're all set" toast that
 *      would trigger CLS or annoy the pilot.
 *   3. Inline form (dropdown + optional company text field) posts to
 *      PATCH /auth/me/profile. On success we broadcast the updated user
 *      to AuthContext so the sidebar reflects the change immediately.
 *   4. Dismissable — but with a soft "we'll ask you next login" (no
 *      permanent hide). Segmentation data is worth the friction.
 *
 * Placement: rendered once inside `DashboardLayout` above the main
 * content so every signed-in page shows it until completed.
 */

import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCheck, Building2, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Session-scoped dismissal — the banner comes back on next page-load so
// we don't lose the segmentation opportunity permanently, but users
// aren't nagged mid-flow.
const DISMISS_KEY = 'dm-profile-banner-dismissed-session';

export default function ProfileCompletionBanner() {
  // Pull the auth token from context so we always read the same source
  // as the rest of the app (localStorage key is 'dronemaps_token', which
  // is an implementation detail we shouldn't hard-couple to here).
  const { user, setUser, token } = useAuth();
  const [profileType, setProfileType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Bail-out cases — cheapest before any hook state calc: not signed in,
  // already complete, or user tapped X this session.
  if (!user) return null;
  if (user.profile_type) return null;
  if (dismissed) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!profileType) {
      toast.error('Merci d\'indiquer si vous êtes professionnel ou amateur.');
      return;
    }
    if (profileType === 'pro' && !companyName.trim()) {
      toast.error('Le nom de la société est requis pour un compte professionnel.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/auth/me/profile`,
        {
          profile_type: profileType,
          // Only send company_name when relevant — sending an empty
          // string for amateur would set it to null server-side which
          // is what we want, but sending "undefined" keeps the payload
          // minimal.
          ...(profileType === 'pro' ? { company_name: companyName.trim() } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Push the fresh user into AuthContext so downstream components
      // (sidebar, admin views) reflect the update without a reload.
      setUser?.(data);
      toast.success('Profil complété — merci !');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de la mise à jour.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-lg border border-sky-200 bg-gradient-to-r from-sky-50 to-white shadow-sm"
      role="region"
      aria-label="Complétez votre profil"
      data-testid="profile-completion-banner"
    >
      <div className="flex items-start gap-4 p-4">
        <div className="shrink-0 w-10 h-10 rounded-full bg-sky-100 border border-sky-200 flex items-center justify-center">
          <UserCheck className="w-5 h-5 text-sky-600" />
        </div>
        <form onSubmit={submit} className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            Complétez votre profil en 10 secondes
          </div>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            Nous vous enverrons des ressources et promotions adaptées à votre usage.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Vous êtes
              </label>
              <select
                value={profileType}
                onChange={(e) => setProfileType(e.target.value)}
                required
                data-testid="profile-banner-type-select"
                className="w-full h-9 px-2 rounded border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 cursor-pointer"
              >
                <option value="" disabled>— Sélectionnez —</option>
                <option value="amateur">Amateur / loisir</option>
                <option value="pro">Professionnel / société</option>
              </select>
            </div>
            {profileType === 'pro' && (
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                  Société
                </label>
                <div className="relative">
                  <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    maxLength={120}
                    placeholder="Ex : Aerial Studio SAS"
                    className="w-full h-9 pl-7 pr-2 rounded border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    data-testid="profile-banner-company-input"
                  />
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="profile-banner-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enregistrer
            </button>
          </div>
        </form>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 text-slate-400 hover:text-slate-600 p-1"
          aria-label="Fermer"
          data-testid="profile-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
